import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Input, Modal, Segmented, Space, Table, Tooltip, Typography, message } from "antd";
import { airlineScheduleIngestApi, ingestErrorMessage } from "../../../api/v3/airlineScheduleIngestApi";
import RotationVisualizer from "../../flightOperation/Monitor/components/RotationVisualizer";
import AirlineIngestSourceButtons from "./AirlineIngestSourceButtons";
import AirlineIngestUploadButton from "./AirlineIngestUploadButton";
import {
    applyFlightRowPatch,
    countUncertainRows,
    fieldNeedsAttention,
    groupFlightsByRotation,
    hasSelectedFlights,
    paperDiffLabel,
    shortUncertainLabel,
} from "./ingestListPresentation";
import "./AirlineIngestReviewModal.css";

const { Text } = Typography;

function cloneFlights(rows) {
    return (rows || []).map((row, index) => {
        const key = `${row.aircraft_reg || "unknown"}-${index}`;
        return {
            key,
            id: key,
            selected: row.selected !== false,
            aircraft_reg: row.aircraft_reg || "",
            aircraft_reg_paper: row.aircraft_reg_paper || row.aircraft_reg || "",
            flight_number: row.flight_number || "",
            departure_airport: row.departure_airport || "",
            departure_airport_paper: row.departure_airport_paper || row.departure_airport || "",
            arrival_airport: row.arrival_airport || "",
            arrival_airport_paper: row.arrival_airport_paper || row.arrival_airport || "",
            std_utc: row.std_utc || "",
            sta_utc: row.sta_utc || "",
            rotation_no: row.rotation_no == null ? "" : String(row.rotation_no),
            uncertain: Boolean(row.uncertain),
            uncertain_reason: row.uncertain_reason || "",
        };
    });
}

function groupByAircraft(rows) {
    const groups = [];
    const index = {};
    rows.forEach((row) => {
        const reg = row.aircraft_reg || "未识别飞机";
        if (index[reg] == null) {
            index[reg] = groups.length;
            groups.push({ aircraft_reg: reg, rows: [] });
        }
        groups[index[reg]].rows.push(row);
    });
    return groups;
}

function payloadFlights(rows) {
    return rows
        .filter((row) => row.selected)
        .map((row) => ({
            aircraft_reg: row.aircraft_reg,
            flight_number: row.flight_number,
            departure_airport: row.departure_airport,
            arrival_airport: row.arrival_airport,
            std_utc: row.std_utc,
            sta_utc: row.sta_utc,
            rotation_no: row.rotation_no,
            uncertain: row.uncertain,
        }));
}

function ReviewCell({ value, onChange, placeholder, note, warn }) {
    return (
        <div className="ingest-review-cell">
            <Input size="small" value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />
            {note ? <div className="ingest-review-note">{note}</div> : null}
            {warn ? <div className="ingest-review-warn">{warn}</div> : null}
        </div>
    );
}

function airportWarn(row, field) {
    return fieldNeedsAttention(row, field) ? "不在名册" : "";
}

function timeWarn(row, field) {
    return fieldNeedsAttention(row, field) ? "看不清" : "";
}

function buildColumns(updateRow, rows) {
    return [
        {
            title: "",
            width: 44,
            render: (_, row) => <Checkbox checked={row.selected} onChange={(event) => updateRow(row.key, { selected: event.target.checked })} />,
        },
        {
            title: <Tooltip title="轮换">ROT</Tooltip>,
            width: 48,
            align: "center",
            render: (_, row) => <RotationVisualizer flight={row} allFlights={rows} />,
        },
        {
            title: "航班号",
            dataIndex: "flight_number",
            width: 92,
            render: (value, row) => <ReviewCell value={value} onChange={(next) => updateRow(row.key, { flight_number: next })} />,
        },
        {
            title: "出发",
            dataIndex: "departure_airport",
            width: 92,
            render: (value, row) => (
                <ReviewCell
                    value={value}
                    note={paperDiffLabel(value, row.departure_airport_paper)}
                    warn={airportWarn(row, "departure_airport")}
                    onChange={(next) => updateRow(row.key, { departure_airport: next.toUpperCase() })}
                />
            ),
        },
        {
            title: "到达",
            dataIndex: "arrival_airport",
            width: 92,
            render: (value, row) => (
                <ReviewCell
                    value={value}
                    note={paperDiffLabel(value, row.arrival_airport_paper)}
                    warn={airportWarn(row, "arrival_airport")}
                    onChange={(next) => updateRow(row.key, { arrival_airport: next.toUpperCase() })}
                />
            ),
        },
        {
            title: "世界时起飞",
            dataIndex: "std_utc",
            width: 188,
            render: (value, row) => (
                <ReviewCell
                    value={value}
                    placeholder="2026-08-02T21:15:00Z"
                    warn={timeWarn(row, "std_utc")}
                    onChange={(next) => updateRow(row.key, { std_utc: next })}
                />
            ),
        },
        {
            title: "世界时到达",
            dataIndex: "sta_utc",
            width: 188,
            render: (value, row) => (
                <ReviewCell
                    value={value}
                    placeholder="2026-08-03T00:15:00Z"
                    warn={timeWarn(row, "sta_utc")}
                    onChange={(next) => updateRow(row.key, { sta_utc: next })}
                />
            ),
        },
        {
            title: "轮班",
            dataIndex: "rotation_no",
            width: 72,
            render: (value, row) => <ReviewCell value={value} onChange={(next) => updateRow(row.key, { rotation_no: next })} />,
        },
        {
            title: "飞机",
            dataIndex: "aircraft_reg",
            width: 108,
            render: (value, row) => (
                <ReviewCell
                    value={value}
                    note={paperDiffLabel(value, row.aircraft_reg_paper)}
                    onChange={(next) => updateRow(row.key, { aircraft_reg: next })}
                />
            ),
        },
        {
            title: "备注",
            dataIndex: "uncertain_reason",
            width: 128,
            render: (_, row) =>
                row.uncertain ? <span className="ingest-review-flag">{shortUncertainLabel(row.uncertain_reason)}</span> : <Text type="secondary">—</Text>,
        },
    ];
}

export default function AirlineIngestReviewModal({
    open,
    taskId,
    onClose,
    onConfirmed,
    onReplaceFile,
    canWrite = false,
}) {
    const [task, setTask] = useState(null);
    const [flights, setFlights] = useState([]);
    const [names, setNames] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [issueOnly, setIssueOnly] = useState(false);

    useEffect(() => {
        if (!open || !taskId) {
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        setIssueOnly(false);
        airlineScheduleIngestApi
            .getTask(taskId)
            .then((response) => {
                if (cancelled) return;
                const payload = response.data;
                setTask(payload);
                setFlights(cloneFlights(payload.draft_flights));
                setNames({ ...(payload.suggested_names || {}) });
            })
            .catch((error) => {
                if (!cancelled) {
                    message.error(ingestErrorMessage(error, "待确认明细打不开"));
                    onClose();
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // onClose 由父组件每次渲染新建，不能放进依赖，否则会反复拉明细
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, taskId]);

    const issueCount = useMemo(() => countUncertainRows(flights), [flights]);
    const groups = useMemo(() => {
        const rows = issueOnly ? flights.filter((row) => row.uncertain) : flights;
        return groupByAircraft(rows);
    }, [flights, issueOnly]);

    const updateRow = (key, patch) => {
        setFlights((current) => current.map((row) => (row.key === key ? applyFlightRowPatch(row, patch) : row)));
    };

    const canConfirm = hasSelectedFlights(flights) && !submitting;
    const empty = flights.length === 0;

    const confirm = async () => {
        if (!canWrite) {
            message.error("没有接入写入权限");
            return;
        }
        if (!hasSelectedFlights(flights)) {
            message.error("请至少勾选一段航班再确认");
            return;
        }
        setSubmitting(true);
        try {
            await airlineScheduleIngestApi.confirmTask(taskId, {
                destination: "ready_to_publish",
                names,
                flights: payloadFlights(flights),
            });
            message.success("已进入待发布，不发航司确认邮件");
            onConfirmed?.("ready_to_publish");
            onClose();
        } catch (error) {
            message.error(ingestErrorMessage(error, "确认失败，整单未写入"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            wrapClassName="airline-ingest-review-modal"
            title={
                <div className="ingest-review-title">
                    <span className="ingest-review-kicker">核对班期</span>
                    {task?.file_name ? (
                        <span className="ingest-review-filename" title={task.file_name}>
                            {task.file_name}
                        </span>
                    ) : null}
                </div>
            }
            open={open}
            onCancel={onClose}
            width="96vw"
            destroyOnHidden
            footer={
                <Space>
                    <Button onClick={onClose}>取消</Button>
                    {canWrite ? (
                        <Button type="primary" disabled={!canConfirm} loading={submitting} onClick={confirm}>
                            确认无误，进入待发布
                        </Button>
                    ) : null}
                </Space>
            }
        >
            {empty ? <Alert type="info" showIcon message="没有抽出可用航班。文件还在，可以换一份或改用手填。" style={{ marginBottom: 12 }} /> : null}
            <div className="ingest-review-toolbar">
                <Space wrap>
                    <AirlineIngestSourceButtons task={task} />
                    {canWrite && onReplaceFile ? <AirlineIngestUploadButton onFile={(file) => onReplaceFile(file, taskId)}>换文件重新识别</AirlineIngestUploadButton> : null}
                </Space>
                {empty ? null : (
                    <Space size="middle">
                        <span className="ingest-review-summary">
                            {flights.length} 段{issueCount ? ` · ${issueCount} 段需核对` : ""}
                        </span>
                        <Segmented
                            size="small"
                            value={issueOnly ? "issues" : "all"}
                            onChange={(value) => setIssueOnly(value === "issues")}
                            options={[
                                { label: "全部", value: "all" },
                                { label: "需核对", value: "issues" },
                            ]}
                        />
                    </Space>
                )}
            </div>
            <div className="ingest-review-board">
                {issueOnly && groups.length === 0 && !empty ? <Alert type="success" showIcon message="没有需要核对的航段。" /> : null}
                {groups.map((group) => {
                    const rotations = groupFlightsByRotation(group.rows);
                    return (
                    <div key={group.aircraft_reg} className="ingest-review-aircraft">
                        <div className="ingest-review-aircraft-head">
                            <span className="ingest-review-reg">{group.aircraft_reg}</span>
                            <span className="ingest-review-meta">
                                {group.rows.length} 段
                                {rotations.length > 1 ? ` · ${rotations.length} 个轮班` : ""}
                                {countUncertainRows(group.rows) ? ` · ${countUncertainRows(group.rows)} 段需核对` : ""}
                            </span>
                            <div className="ingest-review-name">
                                <Input
                                    size="small"
                                    value={names[group.aircraft_reg] || ""}
                                    onChange={(event) => setNames((current) => ({ ...current, [group.aircraft_reg]: event.target.value }))}
                                    placeholder="建议计划名称"
                                />
                            </div>
                        </div>
                        <Table
                            size="small"
                            pagination={false}
                            rowKey="key"
                            loading={loading}
                            dataSource={group.rows}
                            columns={buildColumns(updateRow, group.rows)}
                            scroll={{ x: 1140 }}
                            rowClassName={(row) => (row.uncertain ? "ingest-review-row-issue" : "")}
                        />
                    </div>
                    );
                })}
            </div>
        </Modal>
    );
}
